import { Draft, produce } from "immer";
import _ from "underscore";
import { UniformatId } from "uniformat";

import DetailedElement from "../../../models/domain/detailed_element";
import PlannedBuildingElement, { PlannedBuildingElementArg } from "../../../models/domain/planned_building_element";
import PlannedBuildingElementConverter from "../../converters/planned_building_element_converter";
import { DETAILED_ELEMENTS_LOADED, DetailedElementsLoadedEvent } from "../../../events/loaded/building_elements/detailed_elements_loaded";
import { DETECTED, DISMISSED, INCLUDED } from "../../../models/domain/enums/deviation_status";
import { DEVIATION_MARKED_AS_NOT_RESOLVED, DeviationMarkedAsNotResolvedEvent } from "../../../events/viewer/deviation_marked_as_not_resolved";
import { DEVIATION_MARKED_AS_RESOLVED, DeviationMarkedAsResolvedEvent } from "../../../events/viewer/deviation_marked_as_resolved";
import { ELEMENT_DETAILS_LOADED, ElementDetailsLoadedEvent } from "../../../events/loaded/building_elements/element_details_loaded";
import { ELEMENTS_EDITED } from "../../../events/viewer/elements_edited";
import { ELEMENTS_PARTIAL_PROGRESS_UPDATED } from "../../../events/viewer/elements_partial_progress_updated";
import { ELEMENTS_STATUSES_UPDATED } from "../../../events/viewer/elements_statuses_updated";
import { ELEMENTS_VERIFIED_UPDATED, ElementsVerifiedUpdatedEvent } from "../../../events/viewer/elements_verified_updated";
import { EXPORTED_TO_BCF, ExportedToBcfEvent } from "../../../events/viewer/exported_to_bcf";
import { MINIMAL_PLANNED_BUILDING_ELEMENTS_LOADED, MinimalPlannedBuildingElementsLoadedEvent } from "../../../events/loaded/building_elements/minimal_planned_building_elements_loaded";
import { PLANNED_BUILDING_ELEMENTS_LOADED, PlannedBuildingElementsLoadedEvent } from "../../../events/loaded/building_elements/planned_building_elements_loaded";
import { PUSH_TO_BIM_UNDONE, PushToBimUndoneEvent } from "../../../events/viewer/push_to_bim_undone";
import { PUSHED_ALL_TO_BIM, PushedAllToBimEvent } from "../../../events/viewer/pushed_all_to_bim";
import { PUSHED_TO_BIM, PushedToBimEvent } from "../../../events/viewer/pushed_to_bim";
import { isBigBimViewerPerformanceOn } from "../../utilities/big_bim_viewer_performance";
import { stripUndefinedsFromObject } from "../../utilities/general";

import type { ApiElementDeviation } from "avvir";
import type { ByFirebaseId } from "type_aliases";
import type { ElementEditedEvent, ElementEditedEventPayload } from "../../../models/domain/element_edited_event";
import type { Reducer } from "redux";
import { ELEMENTS_EXCLUDE_FROM_ANALYSIS_UPDATED } from "../../../events/viewer/elements_exclude_from_analysis_updated";

type PlannedBuildingElementsEvents =
  | DetailedElementsLoadedEvent
  | PushedToBimEvent
  | PushToBimUndoneEvent
  | DeviationMarkedAsResolvedEvent
  | DeviationMarkedAsNotResolvedEvent
  | ElementDetailsLoadedEvent
  | ElementEditedEvent
  | MinimalPlannedBuildingElementsLoadedEvent
  | PlannedBuildingElementsLoadedEvent
  | ElementsVerifiedUpdatedEvent
  | ExportedToBcfEvent
  | PushedAllToBimEvent;

type ElementsForFloor = {
  byGlobalId: { [globalId: string]: PlannedBuildingElement }
  /** Only the minimal payload (no deviation, builtAt...) has loaded for the floor so far. */
  onlyMinimal?: boolean
}
export type PlannedBuildingElementsStore = {
  byFirebaseFloorId: ByFirebaseId<ElementsForFloor>
  /**
   * Pages of a full load that's upgrading a floor's elements, built but held back until its last page lands.
   * Nothing reads them: publishing every page made the whole floor's selectors run once per page.
   */
  stagedPagesByFirebaseFloorId?: ByFirebaseId<PlannedBuildingElement[][]>
}

type PartialElementsForFloor = { [globalId: string]: PlannedBuildingElementArg } | PlannedBuildingElementArg[];

const getElementsForFloor = (plannedBuildingElements: Draft<PlannedBuildingElementsStore>, floorId: string) => {
  if (!plannedBuildingElements.byFirebaseFloorId[floorId]) {
    plannedBuildingElements.byFirebaseFloorId[floorId] = {
      byGlobalId: {}
    };
  }
  return plannedBuildingElements.byFirebaseFloorId[floorId].byGlobalId;
};

const updateElementsForFloor = (plannedBuildingElements: Draft<PlannedBuildingElementsStore>, floorId: string, plannedElementsForFloor: (PartialElementsForFloor | ((plannedElementsForFloor: Record<string, PlannedBuildingElement>) => {
  [globalId: string]: PlannedBuildingElementArg
}))) => {
  let elementsForFloor: { [globalId: string]: PlannedBuildingElementArg };
  if (typeof plannedElementsForFloor === "function") {
    elementsForFloor = plannedElementsForFloor(plannedBuildingElements?.byFirebaseFloorId?.[floorId]?.byGlobalId || {});
  } else if (Array.isArray(plannedElementsForFloor)) {
    elementsForFloor = _.indexBy(plannedElementsForFloor, "globalId");
  } else {
    elementsForFloor = plannedElementsForFloor;
  }

  _.forEach(elementsForFloor, (element, globalId) => {
    updateElement(getElementsForFloor(plannedBuildingElements, floorId), globalId, element);
  });

  return plannedBuildingElements;
};

// Before bigBimViewerPerformance a selected element's details were written into the floor's elements (now
// reduce_element_details keeps them).
const updateLoadedElementIfChanged = (plannedBuildingElements: Draft<PlannedBuildingElementsStore>, floorId: string, globalId: string, element: DetailedElement) => {
  return updateFloorElement(plannedBuildingElements, floorId, globalId, { ...element, loaded: true });
};

const updateFloorElement = (plannedBuildingElements: Draft<PlannedBuildingElementsStore>, floorId: string, globalId: string, element: DetailedElement) => {
  if (_.isEqual(element, plannedBuildingElements.byFirebaseFloorId[floorId]?.byGlobalId?.[element.globalId])) {
    console.log(element, "already exists in the store. Skipping store update.");
    return plannedBuildingElements;
  } else {
    return updateElementsForFloor(plannedBuildingElements, floorId, elementsForFloor => {
      // @ts-ignore
      return updateElement(elementsForFloor, globalId, oldElement => {
        const fromDetails = PlannedBuildingElementConverter.detailedElementToPlannedBuildingElement(element);
        const updatedElement = new PlannedBuildingElement({
          ...oldElement,
          ...fromDetails
        });
        const result = stripUndefinedsFromObject(updatedElement);
        return result;
      });
    });
  }
};

const performEditAction = (plannedBuildingElements: Draft<PlannedBuildingElementsStore>, payload: ElementEditedEventPayload) => {
  const floorId = payload.floorId;
  const elementsForFloor = getElementsForFloor(plannedBuildingElements, floorId);
  payload.nextElementStates.forEach((element) => {
    return updateElement(elementsForFloor, element.globalId, () => element);
  });
};

function updateDeviatedElement(plannedBuildingElements: Draft<PlannedBuildingElementsStore>, floorId: string, deviationGlobalId: string, deviation: Partial<ApiElementDeviation>) {
  return updateElement(getElementsForFloor(plannedBuildingElements, floorId), deviationGlobalId, (element) => {
    return {
      ...element,
      deviation: {
        ...element.deviation,
        ...deviation
      }
    };
  });
}

function updateFixedDeviatedElement(plannedBuildingElements: PlannedBuildingElementsStore, floorId: string, deviationGlobalId: string, deviation: Partial<ApiElementDeviation>) {
  return updateElement(getElementsForFloor(plannedBuildingElements, floorId), deviationGlobalId, (element) => {
    return {
      ...element,
      fixedDeviation: {
        ...element.fixedDeviation,
        ...deviation
      }
    };
  });
}

type PlannedBuildingElementFactory = (element: PlannedBuildingElement) => PlannedBuildingElementArg

function getUpdatedElement(originalElement: PlannedBuildingElement, globalId: string, element: PlannedBuildingElementArg | PlannedBuildingElementFactory) {
  if (typeof element === "function") {
    return new PlannedBuildingElement({ ...element(originalElement), globalId });
  } else {
    return new PlannedBuildingElement({ ...originalElement, ...element, globalId });
  }
}

function updateElement(elementsForFloor: Draft<Record<string, PlannedBuildingElement>>, globalId: string, element: PlannedBuildingElementArg | PlannedBuildingElementFactory) {
  if (elementsForFloor[globalId]) {
    elementsForFloor[globalId] = getUpdatedElement(elementsForFloor[globalId], globalId, element);
  } else {
    elementsForFloor[globalId] = getUpdatedElement(new PlannedBuildingElement({ globalId }), globalId, element);
  }

  return elementsForFloor;
}


// The bulk paths build the floor's map outside immer. Immer deep-freezes whatever passes through produce (even
// unchanged), so the next action this reducer saw walked all of a big floor's elements. Freezing the map stops
// that walk at the map, as immer would leave it.
const freezeForImmer = (byGlobalId: object) => Object.freeze(byGlobalId);

const buildFullElement = (prior: PlannedBuildingElement | undefined, element: PlannedBuildingElementArg & { globalId: string }) => {
  if (prior == null) {
    return new PlannedBuildingElement(element);
  } else if (prior.builtStatus != null) {
    // The element came from the minimal payload (eg: Visual Progress, then a switch to another mode),
    // whose fields the full one has too: build from it alone, rather than spreading both into a new
    // object per element (1.34M of them on a big floor). builtStatus is left off deliberately, or
    // getScanLabel would keep short-circuiting on the gateway's snapshot and ignore the scan date.
    const upgraded = new PlannedBuildingElement(element);
    upgraded.trade ??= prior.trade;
    upgraded.uniformat ??= prior.uniformat;
    upgraded.excludeFromAnalysis ??= prior.excludeFromAnalysis;
    return upgraded;
  } else {
    return new PlannedBuildingElement({ ...prior, ...element, globalId: element.globalId, builtStatus: undefined });
  }
};

const reducePlannedBuildingElements: Reducer<PlannedBuildingElementsStore, PlannedBuildingElementsEvents> = (plannedBuildingElements: PlannedBuildingElementsStore = { byFirebaseFloorId: {} }, event) => {
  if (!plannedBuildingElements?.byFirebaseFloorId) {
    plannedBuildingElements.byFirebaseFloorId = {};
  }
  // Bulk-ingest path for a whole-floor element load, taken only when the dispatcher set `bulkIngest`
  // from the bigBimMinimalPayload flag. Without it we fall through to the pre-big-BIM immer branch
  // below, which writes one element at a time into a draft, re-navigating it per element via
  // getElementsForFloor.
  // NOTE: the `{ ...existing }` copy makes this O(elements already loaded) per published page. A first load
  // publishes each page; an upgrade stages its pages and copies once, on the last.
  if (event.type === PLANNED_BUILDING_ELEMENTS_LOADED && event.payload.bulkIngest && !isBigBimViewerPerformanceOn()) {
    const { floorId, elements } = event.payload;
    const existing = plannedBuildingElements.byFirebaseFloorId[floorId]?.byGlobalId;
    const byGlobalId: { [globalId: string]: PlannedBuildingElement } = existing ? { ...existing } : {};
    for (const element of elements) {
      const prior = byGlobalId[element.globalId];
      byGlobalId[element.globalId] = prior
        // builtStatus is cleared deliberately. If the element arrived from the minimal payload first
        // (eg: Visual Progress, then a switch to another mode), the spread would otherwise preserve the
        // gateway's snapshot and getScanLabel would keep short-circuiting on it — leaving scan labels
        // unresponsive to the scan-date selector even though real builtAt/deviation data is now here.
        ? new PlannedBuildingElement({ ...prior, ...element, globalId: element.globalId, builtStatus: undefined })
        : new PlannedBuildingElement(element);
    }
    return {
      ...plannedBuildingElements,
      byFirebaseFloorId: {
        ...plannedBuildingElements.byFirebaseFloorId,
        [floorId]: { byGlobalId }
      }
    };
  }
  if (event.type === PLANNED_BUILDING_ELEMENTS_LOADED && event.payload.bulkIngest) {
    const { floorId, elements, morePages } = event.payload;
    const existing = plannedBuildingElements.byFirebaseFloorId[floorId]?.byGlobalId;
    const { [floorId]: stagedPages = [], ...otherStagedPages } = plannedBuildingElements.stagedPagesByFirebaseFloorId ?? {};
    if (morePages && existing) {
      // The floor already shows its elements (eg: the minimal payload, entering Deviation Analysis), so hold
      // this page back: publishing it would recompute every color group and filter only to redo them all
      // when the next page lands. A first load still publishes each page, so the model colors in as it loads.
      const built = elements.map((element) => buildFullElement(existing[element.globalId], element));
      return {
        ...plannedBuildingElements,
        stagedPagesByFirebaseFloorId: { ...otherStagedPages, [floorId]: [...stagedPages, built] }
      };
    }
    const byGlobalId: { [globalId: string]: PlannedBuildingElement } = existing ? { ...existing } : {};
    for (const page of stagedPages) {
      for (const element of page) {
        byGlobalId[element.globalId] = element;
      }
    }
    for (const element of elements) {
      byGlobalId[element.globalId] = buildFullElement(byGlobalId[element.globalId], element);
    }
    freezeForImmer(byGlobalId);
    return {
      ...plannedBuildingElements,
      byFirebaseFloorId: {
        ...plannedBuildingElements.byFirebaseFloorId,
        [floorId]: { byGlobalId }
      },
      stagedPagesByFirebaseFloorId: otherStagedPages
    };
  }
  // Bulk-ingest path for the minimal payload. Deliberately builds plain objects rather than
  // PlannedBuildingElement instances. Safe because the store is only ever read via static methods and field access
  if (event.type === MINIMAL_PLANNED_BUILDING_ELEMENTS_LOADED) {
    const { floorId, elements } = event.payload;
    const existing = plannedBuildingElements.byFirebaseFloorId[floorId]?.byGlobalId;
    const byGlobalId: { [globalId: string]: PlannedBuildingElement } = existing ? { ...existing } : {};
    for (const element of elements) {
      const prior = byGlobalId[element.globalId];
      if (prior && prior.builtStatus == null) {
        // The prior element already came through the full/detail path (which clears builtStatus), so
        // it has strictly richer data than this minimal page. This happens when a minimal page lands
        // after a full load for the same element — e.g. the user switched from Visual Progress to
        // Deviation Analysis while a multi-page minimal load was still in flight. Drop the stale
        // minimal page; re-adding builtStatus would make getScanLabel short-circuit on a snapshot and
        // stop responding to the scan-date selector.
        byGlobalId[element.globalId] = prior;
      } else if (prior) {
        // Prior is itself a minimal element (still has builtStatus). Spread the whole minimal element
        // over it so fields the endpoint adds later flow through; only the uniformat cast is on top.
        byGlobalId[element.globalId] = new PlannedBuildingElement({
          ...prior,
          ...element,
          uniformat: element.uniformat as UniformatId
        });
      } else {
        byGlobalId[element.globalId] = {
          // Spread rather than hand-pick so a new minimal field isn't silently dropped here.
          ...element,
          uniformat: element.uniformat as UniformatId,
          // Defaulted rather than left undefined: BIM Prep's filter branches on truthiness, and the
          // PlannedBuildingElement constructor doesn't default this either, so false matches what an
          // element loaded through the full path looks like when the column is null.
          excludeFromAnalysis: element.excludeFromAnalysis ?? false
        } as PlannedBuildingElement;
      }
    }
    if (!isBigBimViewerPerformanceOn()) {
      return {
        ...plannedBuildingElements,
        byFirebaseFloorId: {
          ...plannedBuildingElements.byFirebaseFloorId,
          [floorId]: { byGlobalId }
        }
      };
    }
    freezeForImmer(byGlobalId);
    const loadedFloor = plannedBuildingElements.byFirebaseFloorId[floorId];
    return {
      ...plannedBuildingElements,
      byFirebaseFloorId: {
        ...plannedBuildingElements.byFirebaseFloorId,
        // A minimal page after a full load doesn't make the floor minimal again.
        [floorId]: { byGlobalId, onlyMinimal: loadedFloor ? loadedFloor.onlyMinimal : true }
      }
    };
  }
  return produce(plannedBuildingElements, (plannedBuildingElementsDraft) => {
    switch (event.type) {
      case DETAILED_ELEMENTS_LOADED: {
        updateElementsForFloor(plannedBuildingElementsDraft, event.payload.floorId, event.payload.elements.reduce((elementsSoFar, element) => {
          elementsSoFar[element.globalId] = PlannedBuildingElementConverter.detailedElementToPlannedBuildingElement(element);
          return elementsSoFar;
        }, {}));
        break;
      }
      case PUSHED_TO_BIM: {
        if (event.payload.isFixedDeviation) {
          updateFixedDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.globalId, { status: INCLUDED });
        } else {
          updateDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.globalId, { status: INCLUDED });
        }
        break;
      }
      case PUSH_TO_BIM_UNDONE: {
        if (event.payload.isFixedDeviation) {
          updateFixedDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DETECTED });
        } else {
          updateDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DETECTED });
        }
        break;
      }
      case DEVIATION_MARKED_AS_RESOLVED: {
        if (event.payload.isFixedDeviation) {
          updateFixedDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DISMISSED });
        } else {
          updateDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DISMISSED });
        }
        break;
      }
      case DEVIATION_MARKED_AS_NOT_RESOLVED: {
        if (event.payload.isFixedDeviation) {
          updateFixedDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DETECTED });
        } else {
          updateDeviatedElement(plannedBuildingElementsDraft, event.payload.floorId, event.payload.deviationGlobalId, { status: DETECTED });
        }
        break;
      }
      case ELEMENT_DETAILS_LOADED: {
        // With bigBimViewerPerformance reduce_element_details keeps them, so a click doesn't replace the floor map.
        if (!isBigBimViewerPerformanceOn()) {
          event.payload.elements.forEach((element) => {
            return updateLoadedElementIfChanged(plannedBuildingElementsDraft, event.payload.floorId, element.globalId, new DetailedElement(element));
          });
        }
        break;
      }
      // Pre-big-BIM ingest, reached when the dispatch carried no `bulkIngest` marker. A dispatch that
      // did carry one is already handled by the bulk-ingest path above, before produce().
      case PLANNED_BUILDING_ELEMENTS_LOADED: {
        updateElementsForFloor(plannedBuildingElementsDraft, event.payload.floorId, event.payload.elements);
        break;
      }
      case ELEMENTS_EDITED:
      case ELEMENTS_STATUSES_UPDATED:
      case ELEMENTS_EXCLUDE_FROM_ANALYSIS_UPDATED:
      case ELEMENTS_PARTIAL_PROGRESS_UPDATED: {
        performEditAction(plannedBuildingElementsDraft, event.payload);
        break;
      }
      case ELEMENTS_VERIFIED_UPDATED: {
        updateElementsForFloor(plannedBuildingElementsDraft, event.payload.firebaseFloorId, event.payload.elementsGlobalIds.reduce((elementsSoFar, globalId) => {
          elementsSoFar[globalId] = { verified: event.payload.verifiedStatus };

          return elementsSoFar;
        }, {}));
        break;
      }
      case EXPORTED_TO_BCF: {
        updateElementsForFloor(plannedBuildingElementsDraft, event.payload.floorId, event.payload.globalIds.reduce((elementsSoFar, globalId) => {
          elementsSoFar[globalId] = { exportedToBcf: true };
          return elementsSoFar;
        }, {}));
        break;
      }
      case PUSHED_ALL_TO_BIM: {
        event.payload.deviations.forEach(deviation => {
          updateDeviatedElement(plannedBuildingElements, event.payload.floorId, deviation.globalId, { status: INCLUDED });
        });
        break;
      }
      default: {
        return plannedBuildingElementsDraft;
      }
    }
  });
};

export default reducePlannedBuildingElements;

